import React from 'react';
import { Navigate } from 'react-router-dom';

import { useAppSelector } from '@/store';

/**
 * Shared auth gate for the hand-written layouts (`MainLayout`, `CustomizerLayout`).
 *
 * Returns `authenticated` (handy as an effect dependency) plus a `gate`:
 *   - `undefined` → the user is authenticated; render the layout normally.
 *   - `null`      → the session is still resolving; render nothing (so a hard
 *                   refresh doesn't bounce a logged-in user through /register).
 *   - element     → not authenticated; render this redirect to /register.
 *
 * Callers run it with their other hooks, then `if (gate !== undefined) return gate;`.
 */
export function useAuthGate(): { authenticated: boolean; gate: React.ReactElement | null | undefined } {
  const authenticated = useAppSelector(
    (state) => Boolean(state.authentication) && state.authentication.auth_type === 'user',
  );
  const authInProgress = useAppSelector((state) => Boolean(state.authentication?.in_progress));

  const gate = authenticated ? undefined : authInProgress ? null : <Navigate to="/register" replace />;

  return { authenticated, gate };
}

export default useAuthGate;